/**
 * Custom-roles API client (Settings area) — talks to mttr/app_api
 * `/api/admin/custom-roles`. Customer admins manage their own company's custom
 * roles (company_id implied by the token); platform admins may target any
 * company by passing `company_id`. All routes gated server-side by
 * `users.manage`; the token rides in the `access-token` header.
 *
 * A custom role is a company-scoped bundle of *grantable* capabilities (the
 * server whitelists which caps a custom role may hold and returns that list as
 * `grantable_caps`). Capability changes take effect on a user's NEXT login,
 * since caps are baked into the signed token.
 */

import { ApiError, apiFetch } from "./apiClient";

/** A company-scoped custom role (migration: custom roles). */
export interface CustomRole {
  role_id: number;
  role_name: string;
  description: string | null;
  company_id: number | null;
  created_utc: string | null;
  /** How many users are currently assigned this role. */
  user_count: number;
  /** The capability keys this role grants. */
  permission_keys: string[];
}

export interface CustomRolesResponse {
  roles: CustomRole[];
  /** Capability keys a custom role is allowed to hold (server whitelist). */
  grantable_caps: string[];
}

export interface CreateCustomRoleBody {
  name: string;
  description?: string | null;
  permission_keys: string[];
  /** Platform callers MUST send this; customer admins MUST NOT (server uses
   *  their own company). */
  company_id?: number;
}

export interface UpdateCustomRoleBody {
  name: string;
  description?: string | null;
  permission_keys: string[];
}

class CustomRolesApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "CustomRolesApiError";
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new CustomRolesApiError(e.message, e.status) : e;
  }
}

/**
 * List custom roles. A customer admin omits `companyId` (scoped to their own
 * company); a platform caller may pass `companyId` to view a specific company
 * (omitting returns all).
 */
export function fetchCustomRoles(
  token: string,
  companyId?: number,
): Promise<CustomRolesResponse> {
  const qs = companyId != null ? `?company_id=${companyId}` : "";
  return request<CustomRolesResponse>(`/api/admin/custom-roles${qs}`, token);
}

export function createCustomRole(
  token: string,
  body: CreateCustomRoleBody,
): Promise<{ role_id: number }> {
  return request<{ role_id: number }>("/api/admin/custom-roles", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateCustomRole(
  token: string,
  roleId: number,
  body: UpdateCustomRoleBody,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/admin/custom-roles/${roleId}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteCustomRole(token: string, roleId: number): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/admin/custom-roles/${roleId}`, token, {
    method: "DELETE",
  });
}

/** Friendly labels for grantable capability keys; falls back to the raw key. */
const CAP_LABELS: Record<string, string> = {
  "fleet.view": "View fleet/map",
  "inventory.view": "View inventory",
  "reports.view": "View reports",
  "inventory.manual_checkout": "Manual checkout",
  "inventory.manual_checkin": "Manual check-in",
  "minmax.edit": "Edit min/max",
  "tags.manage": "Manage RFID tags",
  "pricing.view": "See pricing",
  "operator.triage": "Operator triage",
  "boxes.stack": "Mark boxes stacked",
  "users.manage": "Manage users & roles (own company)",
  "companies.manage": "Manage companies",
  "dashboard.fleet_health": "Dashboard: fleet health",
  "dashboard.tag_health": "Dashboard: tag health",
  "dashboard.starlink": "Dashboard: Starlink",
  "dashboard.peplink": "Dashboard: Peplink",
};

export function capLabel(key: string): string {
  return CAP_LABELS[key] ?? key;
}
